import {useMemo} from 'react';
import {
  Action,
  AnyAction,
  ThunkAction,
  ThunkDispatch,
  configureStore,
} from '@reduxjs/toolkit';
import {createWrapper} from 'next-redux-wrapper';
import thunk from 'redux-thunk';
import {combineReducers} from 'redux';

import {
  authState,
  appState,
  appReducer,
  authReducer,
  jobState,
  jobDetailState,
  jobReducer,
  jobDetailReducer,
  userReducer,
  adsReducer,
  competitionReducer,
  botDetectionReducer,
} from '@jobseeker/common-core/reducers';

const initialState: object = {
  job: jobState,
  auth: authState,
  app: appState,
  jobDetail: jobDetailState,
};

let store: any;

const rootReducer = combineReducers({
  ads: adsReducer,
  auth: authReducer,
  app: appReducer,
  job: jobReducer,
  user: userReducer,
  jobDetail: jobDetailReducer,
  competition: competitionReducer,
  botDetection: botDetectionReducer,
});

const initStore = (preloadedState = initialState) => {
  return configureStore({
    reducer: rootReducer,
    preloadedState: preloadedState,
    devTools: process.env.NODE_ENV !== 'production',
    middleware: [thunk],
  });
};

export const initializeStore = (preloadedState: object) => {
  let _store = store ?? initStore(preloadedState);

  if (preloadedState && store) {
    _store = initStore({
      ...store.getState(),
      ...preloadedState,
    });

    store = undefined;
  }

  if (typeof window === 'undefined') return _store;

  if (!store) store = _store;

  return store;
};

export const useStore = (initialState: object) => {
  const store = useMemo(() => initializeStore(initialState), [initialState]);
  return store;
};

export type AppStore = ReturnType<typeof initializeStore>;
export type AppState = ReturnType<AppStore['getState']>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  AppState,
  unknown,
  Action
>;
export type AppThunkDispatch = ThunkDispatch<AppState, void, AnyAction>;

export const wrapper = createWrapper(initializeStore);
